import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/api/apiService";
import { getClaim, acceptClaim, registerAndClaim } from "../../services/api/userService";
import { PASSWORD_RULE, passwordProblem } from "../../utils/passwordPolicy";
import "../../styles/features/auth.css";

const ACCOUNT_EXISTS_FALLBACK =
  "You already have a Sportifide account with this email. Sign in to claim your profile — if you forgot your password, use Forgot password.";

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

/** "Alexander's Muirs in Gladstone Tuesday Volleyball" */
const placeLabel = (t) => [t.teamName, t.leagueName].filter(Boolean).join(" in ");

/**
 * /claim/:token — a league host added this person (or imported them) and
 * emailed a one-time link. They create an account (name prefilled, email from
 * the invite) or sign in; the placeholder profile, roster spot and match
 * history then become theirs. An email that already has an account is never
 * signed up again: sign in (or reset the password) to claim.
 */
function ClaimProfile() {
  const { token } = useParams();
  const { isAuthenticated, user, login } = useAuth();

  const [claim, setClaim] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [mode, setMode] = useState("register"); // register | signin
  const [accountExists, setAccountExists] = useState(false);
  const [form, setForm] = useState({ name: "", password: "", confirm: "", email: "", signinPassword: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let active = true;
    getClaim(token)
      .then((res) => {
        if (!active) return;
        const data = res.data;
        setClaim(data);
        setForm((f) => ({ ...f, name: data.player?.name || "", email: data.email || "" }));
        if (data.accountExists) {
          setAccountExists(true);
          setMode("signin");
        }
      })
      .catch((err) => active && setLoadError(errorText(err, "This claim link is invalid.")));
    return () => {
      active = false;
    };
  }, [token]);

  const change = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const finish = (res) => setDone(res?.data || {});

  const claimSignedIn = async () => {
    setBusy(true);
    setError("");
    try {
      finish(await acceptClaim(token));
    } catch (err) {
      setError(errorText(err, "Could not claim this profile."));
    } finally {
      setBusy(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const problem = passwordProblem(form.password);
    if (problem) return setError(problem);
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError("");
    try {
      const res = await registerAndClaim(token, { name: form.name.trim(), password: form.password });
      const { user: newUser, token: sessionToken } = res.data || {};
      if (sessionToken) localStorage.setItem("token", sessionToken);
      if (newUser) {
        localStorage.setItem("user", JSON.stringify(newUser));
        login(null, newUser);
      }
      finish(res);
    } catch (err) {
      if (err?.response?.data?.code === "ACCOUNT_EXISTS") {
        setAccountExists(true);
        setMode("signin");
        return;
      }
      setError(errorText(err, "Could not create your account."));
    } finally {
      setBusy(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await apiService.loginUser({ email: form.email, password: form.signinPassword });
      const signedIn = res.user || res.data?.user;
      const sessionToken = res.token || res.data?.token;
      if (!signedIn) throw new Error("Sign-in failed");
      if (sessionToken) localStorage.setItem("token", sessionToken);
      localStorage.setItem("user", JSON.stringify(signedIn));
      login(null, signedIn);
      finish(await acceptClaim(token));
    } catch (err) {
      const code = err?.response?.data?.code;
      setError(
        code === "INVALID_CREDENTIALS" || code === "USER_NOT_FOUND"
          ? "Invalid email or password"
          : errorText(err, err.message || "Sign-in failed")
      );
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="auth-container">
        <div className="auth-form" style={{ textAlign: "center" }}>
          <h2>Claim link not available</h2>
          <p role="alert" className="error-message">{loadError}</p>
          <div className="auth-links">
            <Link to="/login">Go to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="auth-container">
        <div className="auth-form" style={{ textAlign: "center" }}>Loading your profile…</div>
      </div>
    );
  }

  const teams = claim.teams || [];
  const primary = teams[0];

  if (done) {
    return (
      <div className="auth-container">
        <div className="auth-form" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✅</div>
          <h2>Profile claimed!</h2>
          <p style={{ color: "#555", marginBottom: "1.5rem" }}>
            You're now {claim.player.name}
            {primary ? ` on ${placeLabel(primary)}` : ""}, with your match history and stats.
          </p>
          <Link to="/dashboard" className="auth-button" style={{ display: "inline-block", textDecoration: "none" }}>
            Go to my dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Claim your profile</h2>
        <p data-testid="claim-who" style={{ fontSize: "1.05rem", marginBottom: "0.5rem" }}>
          You're <strong>{claim.player.name}</strong>
          {primary?.teamName && <> on <strong>{primary.teamName}</strong></>}
          {primary?.leagueName && <> in <strong>{primary.leagueName}</strong></>}.
        </p>
        {teams.length > 1 && (
          <ul style={{ color: "#555", fontSize: "0.9rem", margin: "0 0 1rem 1.2rem" }}>
            {teams.slice(1).map((t) => (
              <li key={`${t.teamId}-${t.leagueId}`}>Also {placeLabel(t)}</li>
            ))}
          </ul>
        )}

        {isAuthenticated ? (
          <>
            <p style={{ color: "#555", marginBottom: "1rem" }}>
              Signed in as <strong>{user?.email || user?.username}</strong>. Claiming adds this roster spot and
              its match history to your account.
            </p>
            <button type="button" className="auth-button" onClick={claimSignedIn} disabled={busy}>
              {busy ? "Claiming…" : "Claim this profile"}
            </button>
          </>
        ) : (
          <>
            {accountExists && (
              <div
                role="status"
                style={{ marginBottom: "1rem", padding: "0.75rem 1rem", borderRadius: 6, background: "#fff7e6", border: "1px solid #f0c36d", color: "#5c4400" }}
              >
                <p style={{ margin: 0 }}>{claim.accountExistsMessage || ACCOUNT_EXISTS_FALLBACK}</p>
                <p style={{ margin: "0.4rem 0 0" }}>
                  <button type="button" className="link-button" onClick={() => setMode("signin")}>Sign in</button>
                  {" · "}
                  <Link to="/forgot-password">Forgot password</Link>
                </p>
              </div>
            )}

            {mode === "register" ? (
              <form onSubmit={handleRegister}>
                <p style={{ color: "#555", fontSize: "0.95rem" }}>Create your account to take over this profile.</p>
                <div className="form-group">
                  <label htmlFor="claim-name">Your name</label>
                  <input id="claim-name" name="name" value={form.name} onChange={change} autoComplete="name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="claim-email">Email</label>
                  <input id="claim-email" name="email" type="email" value={claim.email || ""} readOnly />
                </div>
                <div className="form-group">
                  <label htmlFor="claim-password">Password</label>
                  <input id="claim-password" name="password" type="password" autoComplete="new-password"
                    value={form.password} onChange={change} aria-describedby="claim-password-rule" required />
                  <small id="claim-password-rule" style={{ display: "block", marginTop: "0.35rem", fontSize: "0.78rem", opacity: 0.75 }}>
                    {PASSWORD_RULE}
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="claim-confirm">Confirm password</label>
                  <input id="claim-confirm" name="confirm" type="password" autoComplete="new-password"
                    value={form.confirm} onChange={change} required />
                </div>
                <button type="submit" className="auth-button" disabled={busy}>
                  {busy ? "Creating…" : "Create account and claim"}
                </button>
                <p style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                  Already have a Sportifide account?{" "}
                  <button type="button" className="link-button" onClick={() => { setMode("signin"); setError(""); }}>
                    Sign in to claim
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSignIn}>
                <div className="form-group">
                  <label htmlFor="claim-signin-email">Email or username</label>
                  <input id="claim-signin-email" name="email" value={form.email} onChange={change} autoComplete="username" required />
                </div>
                <div className="form-group">
                  <label htmlFor="claim-signin-password">Password</label>
                  <input id="claim-signin-password" name="signinPassword" type="password" autoComplete="current-password"
                    value={form.signinPassword} onChange={change} required />
                </div>
                <button type="submit" className="auth-button" disabled={busy}>
                  {busy ? "Signing in…" : "Sign in and claim"}
                </button>
                <p style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                  <Link to="/forgot-password">Forgot password?</Link>
                  {!accountExists && (
                    <>
                      {" · "}
                      <button type="button" className="link-button" onClick={() => { setMode("register"); setError(""); }}>
                        Create an account instead
                      </button>
                    </>
                  )}
                </p>
              </form>
            )}
          </>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}
      </div>
    </div>
  );
}

export default ClaimProfile;
